import React, { useEffect, useRef, useState } from 'react';
import { useManageContext } from '../../contexts/ManageContext/ManageContext';
import { makeStyles, Theme, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import CameraIcon from '@material-ui/icons/Cached';

// eslint-disable-next-line
import { BrowserMultiFormatReader } from '@zxing/library';
import Measure from 'react-measure';

const BC_CAMERA_KEY = 'barcode.camera';

const useStyles = makeStyles((theme: Theme) => ({
  scanner: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    zIndex: theme.zIndex.modal + 100,
  },
  close: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: 'white',
  },
  camera: {
    position: 'absolute',
    top: theme.spacing(1),
    left: theme.spacing(1),
    color: 'white',
  },
  cover: {
    objectFit: 'cover',
  },
  body: {
    overflowY: 'hidden',
  },
  viewport: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
  },
  verticalOffset: {
    flexGrow: 1,
    backgroundColor: 'black',
    opacity: 0.25,
  },
  scanbox: {
    height: 200,
    display: 'flex',
  },
  horizontalOffset: {
    width: theme.spacing(2),
    backgroundColor: 'black',
    opacity: 0.25,
  },
  barcodeBox: {
    position: 'relative',
    flexGrow: 1,
    borderRadius: theme.spacing(2),
    '& div': {
      position: 'absolute',
      width: theme.spacing(4),
      height: theme.spacing(4),
      zIndex: theme.zIndex.modal + 200,
    },
    '& .tl': {
      borderTopLeftRadius: 4,
      borderTop: 'solid 2px white',
      borderLeft: 'solid 2px white',
      top: -2,
      left: -2,
    },
    '& .tr': {
      borderTopRightRadius: 4,
      borderTop: 'solid 2px white',
      borderRight: 'solid 2px white',
      top: -2,
      right: -2,
    },
    '& .bl': {
      borderBottomLeftRadius: 4,
      borderBottom: 'solid 2px white',
      borderLeft: 'solid 2px white',
      bottom: -2,
      left: -2,
    },
    '& .br': {
      borderBottomRightRadius: 4,
      borderBottom: 'solid 2px white',
      borderRight: 'solid 2px white',
      bottom: -2,
      right: -2,
    },
  },
}));

let reader: BrowserMultiFormatReader | null = null;

interface Vid {
  selected: number | null;
  devices: string[];
}

const BarcodeScanner = () => {
  const classes = useStyles();
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [vid, setVid] = useState<Vid>({
    selected: null,
    devices: [],
  });

  const { state, dispatch } = useManageContext();
  const visible = state.barcode && state.barcode.visible;

  const scanRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    reader = new BrowserMultiFormatReader();

    reader.listVideoInputDevices().then((vid) => {
      let camera = localStorage.getItem(BC_CAMERA_KEY);
      let selected = 0;

      if (camera !== null) {
        selected = parseInt(camera);
      }

      if (vid && vid.length > 0) {
        setVid({
          selected: selected,
          devices: vid.map((item) => item.deviceId),
        });
      }
    });
  }, []);

  useEffect(() => {
    if (visible) {
      document.documentElement.classList.add(classes.body);

      return () => {
        document.documentElement.classList.remove(classes.body);
      };
    }

    document.documentElement.classList.remove(classes.body);
  }, [classes.body, visible]);

  useEffect(() => {
    if (
      scanRef &&
      scanRef.current !== null &&
      reader !== null &&
      vid.devices.length > 0 &&
      vid.selected !== null
    ) {
      const selectedDevice = vid.devices[vid.selected];

      if (scanRef.current != null && reader !== null) {
        reader
          .decodeOnceFromVideoDevice(selectedDevice, scanRef.current)
          .then((result: any) => {
            if (reader != null) {
              reader.reset();
              dispatch({
                type: 'barcode-changed',
                data: { visible: false, value: result.text },
              });
            }
          });

        return () => {
          if (reader !== null) {
            reader.reset();
          }
        };
      }
    }
  }, [visible, dispatch, vid.devices, vid.selected]);

  const handleResize = (data: any) => {
    if (data) {
      setSize(data.bounds);
    }
  };

  const selectNextCamera = () => {
    if (vid.selected != null) {
      let next = vid.selected + 1;
      const max = vid.devices.length;

      if (next >= max) {
        next = 0;
      }

      localStorage.setItem(BC_CAMERA_KEY, next.toString());
      setVid({ ...vid, selected: next });
    }
  };

  if (visible) {
    return (
      <Measure bounds onResize={handleResize}>
        {({ measureRef }) => (
          <div ref={measureRef} className={classes.scanner}>
            <video
              className={classes.cover}
              ref={scanRef}
              width={size.width}
              height={size.height}
            />

            <div className={classes.viewport}>
              <div className={classes.verticalOffset}></div>
              <div className={classes.scanbox}>
                <div className={classes.horizontalOffset}></div>
                <div className={classes.barcodeBox}>
                  <div className="tl"></div>
                  <div className="tr"></div>
                  <div className="bl"></div>
                  <div className="br"></div>
                </div>
                <div className={classes.horizontalOffset}></div>
              </div>
              <div className={classes.verticalOffset}></div>
            </div>

            <IconButton className={classes.camera} onClick={selectNextCamera}>
              <CameraIcon />
            </IconButton>
            <IconButton
              className={classes.close}
              onClick={() =>
                dispatch({ type: 'barcode-changed', data: { visible: false } })
              }
            >
              <CloseIcon />
            </IconButton>
          </div>
        )}
      </Measure>
    );
  }

  return null;
};

export default BarcodeScanner;
